import { FORMATS, DEFAULT_OG_IMAGE, SITE_NAME } from "@/utils/Const";

const CTX = "https://schema.org";
const FORMAT_MAP = {
	[FORMATS.epub]: `${CTX}/EBook`,
	[FORMATS.pdf]: `${CTX}/EBook`,
	[FORMATS.print]: `${CTX}/Paperback`,
	paperback: `${CTX}/Paperback`,
	hardcover: `${CTX}/Hardcover`,
	hardback: `${CTX}/Hardcover`,
	[FORMATS.audio]: `${CTX}/AudiobookFormat`,
};
const DEFAULT_FORMAT = FORMAT_MAP[FORMATS.epub];

function extractAuthors(bookData) {
	if (Array.isArray(bookData.authors)) {
		return bookData.authors
			.filter(Boolean)
			.map((name) => ({ "@type": "Person", name }));
	}
	if (Array.isArray(bookData.bookAuthors)) {
		return bookData.bookAuthors
			.filter((ba) => ba.author?.name)
			.map((ba) => ({ "@type": "Person", name: ba.author.name }));
	}
	return [];
}

function extractPublisherName(bookData) {
	const pub = bookData.publisher;
	if (typeof pub === "string") return pub || null;
	return pub?.name || null;
}

function resolveFormat(format) {
	if (!format) return DEFAULT_FORMAT;
	const key =
		typeof format === "object" ? format._id || format.mimeType : format;
	return FORMAT_MAP[key] || DEFAULT_FORMAT;
}

function baseFields(bookData) {
	const fields = { name: bookData.title };
	fields.image = bookData.imageUrl || DEFAULT_OG_IMAGE;
	if (bookData.description) fields.description = bookData.description;
	return fields;
}

/** Generates a schema.org Book JSON-LD object. */
export function generateBookSchema(bookData) {
	if (!bookData?.title) return null;
	const schema = {
		"@context": CTX,
		"@type": "Book",
		...baseFields(bookData),
		bookFormat: resolveFormat(bookData.format),
	};
	if (bookData.isbn) schema.isbn = bookData.isbn;
	if (bookData.language) schema.inLanguage = bookData.language;
	if (bookData.publishedOn) schema.datePublished = bookData.publishedOn;
	else if (bookData.modified) schema.datePublished = bookData.modified;
	const authors = extractAuthors(bookData);
	if (authors.length)
		schema.author = authors.length === 1 ? authors[0] : authors;
	const pub = extractPublisherName(bookData);
	if (pub) schema.publisher = { "@type": "Organization", name: pub };
	return schema;
}

/** Generates a schema.org Product JSON-LD object with Offer. */
export function generateProductSchema(bookData, priceData) {
	if (!bookData?.title) return null;
	const schema = {
		"@context": CTX,
		"@type": "Product",
		...baseFields(bookData),
	};
	if (bookData.isbn) {
		schema.sku = bookData.isbn;
		const digits = bookData.isbn.replace(/[^0-9]/g, "");
		if (digits.length === 13) schema.gtin13 = digits;
	}
	const pub = extractPublisherName(bookData);
	if (pub) schema.brand = { "@type": "Organization", name: pub };
	if (priceData?.price != null && priceData.currencyCode) {
		schema.offers = {
			"@type": "Offer",
			price: priceData.price,
			priceCurrency: priceData.currencyCode,
			availability:
				bookData.available === false ? `${CTX}/OutOfStock` : `${CTX}/InStock`,
			seller: { "@type": "Organization", name: SITE_NAME },
		};
	}
	return schema;
}

/** Generates a schema.org BreadcrumbList JSON-LD object. */
export function generateBreadcrumbSchema(breadcrumbs) {
	if (!breadcrumbs?.length) return null;
	return {
		"@context": CTX,
		"@type": "BreadcrumbList",
		itemListElement: breadcrumbs.map((crumb, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: crumb.text,
			...(crumb.href ? { item: crumb.href } : {}),
		})),
	};
}

/** Injects or updates a JSON-LD script tag in document.head. */
export function injectJsonLd(id, schema) {
	const elementId = `json-ld-${id}`;
	const existing = document.getElementById(elementId);
	if (!schema) {
		if (existing) existing.remove();
		return;
	}
	const el = existing || document.createElement("script");
	if (!existing) {
		el.id = elementId;
		el.type = "application/ld+json";
		document.head.appendChild(el);
	}
	el.textContent = JSON.stringify(schema).replace(/<\//g, "<\\/");
}
